/**
 * BlockedSourcesPanel — right-rail "Blocked" tab (FEAT-017).
 *
 * Lists fetch_url failures Scout/Librarian hit for this piece (paywall,
 * bot-block, empty result, timeout, unknown), so the operator can review
 * and manually resolve them by uploading content or skipping. Self-contained
 * fetch, same pattern as TokenUsagePanel.tsx, rather than threading data
 * through PieceView/RightRail — this panel owns its own query and mutations.
 *
 * Phase 3 — upload widget (file / url / paste, matching LibraryView.tsx's
 * UploadPanel three modes) + skip/reopen, posting to
 * POST/PATCH /pieces/:id/blocked-sources/:bid.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi, useApiQuery } from "@/lib/api";
import { ageString } from "@/lib/format";
import type { PieceBlockedSource } from "@/types/piece";
import { useRole } from "@/lib/useRole";

interface Props {
  pieceId: number;
}

const FAILURE_LABELS: Record<PieceBlockedSource["failure_reason"], string> = {
  paywall: "Paywall",
  bot_block: "Bot-blocked",
  no_result: "No result",
  timeout: "Timeout",
  unknown: "Unknown",
};

const RESOLUTION_PILL: Record<PieceBlockedSource["resolution_status"], string> = {
  unresolved: "pill-warn",
  manual_upload: "pill-ok",
  skipped: "pill-muted",
};

// Q5 (spec) — no hard cap on blocked sources per piece, but a high count
// signals a deeper Scout/Librarian problem worth flagging.
const HIGH_COUNT_WARNING_THRESHOLD = 10;

export function BlockedSourcesPanel({ pieceId }: Props) {
  const { data, isLoading } = useApiQuery<{ data: PieceBlockedSource[] }>(
    ["piece-blocked-sources", pieceId],
    `/pieces/${pieceId}/blocked-sources`
  );
  const rows = data?.data ?? [];
  // Only one row's upload form open at a time — simpler than tracking a set.
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <div className="panel-body">
      <div className="panel-header">
        <h3>Blocked Sources</h3>
        <span className="panel-sub">
          {rows.length} URL{rows.length === 1 ? "" : "s"} the pipeline couldn't fetch
        </span>
      </div>

      {isLoading && <div className="panel-empty">Loading…</div>}

      {!isLoading && rows.length === 0 && (
        <div className="panel-empty">
          No blocked sources yet. This piece's research fetched cleanly.
        </div>
      )}

      {rows.length >= HIGH_COUNT_WARNING_THRESHOLD && (
        <div className="blocked-sources-warning">
          High blocked-source count may indicate a Scout/Librarian problem.
        </div>
      )}

      {rows.length > 0 && (
        <ul className="report-list">
          {rows.map((row) => (
            <BlockedSourceRow
              key={row.id}
              pieceId={pieceId}
              row={row}
              expanded={expandedId === row.id}
              onToggleExpand={() => setExpandedId(expandedId === row.id ? null : row.id)}
              onResolved={() => setExpandedId(null)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function BlockedSourceRow({
  pieceId,
  row,
  expanded,
  onToggleExpand,
  onResolved,
}: {
  pieceId: number;
  row: PieceBlockedSource;
  expanded: boolean;
  onToggleExpand: () => void;
  onResolved: () => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const resolved = row.resolution_status !== "unresolved";
  // FEAT-108 / S-438 — resolving a source is Admin and Editor work.
  const { canEdit } = useRole();

  const skip = useMutation({
    mutationFn: () =>
      api(`/pieces/${pieceId}/blocked-sources/${row.id}`, {
        method: "PATCH",
        body: JSON.stringify({ resolution_status: "skipped" }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece-blocked-sources", pieceId] });
      onResolved();
    },
  });

  return (
    <li className="blocked-source-row">
      <div className="blocked-source-top">
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          className="blocked-source-url"
          title={row.url}
        >
          {row.url}
        </a>
        <span className={`pill ${RESOLUTION_PILL[row.resolution_status]}`}>
          {resolved ? (row.resolution_status === "manual_upload" ? "Resolved" : "Skipped") : "Unresolved"}
        </span>
      </div>
      <div className="blocked-source-bottom">
        <span className="report-row-sub">
          {row.resolution_status === "manual_upload"
            ? `Resolved → library doc #${row.library_document_id}`
            : `${FAILURE_LABELS[row.failure_reason]} · ${ageString(row.attempted_at)}`}
        </span>
        {!resolved && canEdit && (
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn-dec btn-review"
              onClick={onToggleExpand}
              title={expanded ? "Collapse" : "Upload the content you fetched yourself"}
            >
              {expanded ? "Cancel" : "Upload content"}
            </button>
            <button
              className="btn-dec btn-reject"
              onClick={() => skip.mutate()}
              disabled={skip.isPending}
              title="Mark as not worth pursuing"
            >
              {skip.isPending ? "…" : "Skip"}
            </button>
          </div>
        )}
      </div>
      {expanded && !resolved && canEdit && (
        <BlockedSourceUploadForm pieceId={pieceId} blockedSourceId={row.id} onDone={onResolved} />
      )}
      {skip.isError && (
        <span className="settings-field-error">
          {skip.error instanceof Error ? skip.error.message : "Failed to skip."}
        </span>
      )}
    </li>
  );
}

type UploadMode = "file" | "url" | "paste";

function BlockedSourceUploadForm({
  pieceId,
  blockedSourceId,
  onDone,
}: {
  pieceId: number;
  blockedSourceId: number;
  onDone: () => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [mode, setMode] = useState<UploadMode>("file");
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [promoteToLibrary, setPromoteToLibrary] = useState(false);

  const upload = useMutation({
    mutationFn: async () => {
      if (mode === "file") {
        if (!file) throw new Error("Choose a file to upload.");
        const form = new FormData();
        form.append("file", file);
        if (name.trim()) form.append("name", name.trim());
        form.append("promote_to_library", String(promoteToLibrary));
        return api(`/pieces/${pieceId}/blocked-sources/${blockedSourceId}/upload`, {
          method: "POST",
          body: form,
        });
      }
      if (mode === "url") {
        if (!url.trim()) throw new Error("Enter a URL.");
        return api(`/pieces/${pieceId}/blocked-sources/${blockedSourceId}/upload`, {
          method: "POST",
          body: JSON.stringify({
            source_type: "url",
            source_url: url.trim(),
            name: name.trim() || undefined,
            promote_to_library: promoteToLibrary,
          }),
        });
      }
      if (!name.trim()) throw new Error("Enter a name.");
      if (!text.trim()) throw new Error("Paste some text.");
      return api(`/pieces/${pieceId}/blocked-sources/${blockedSourceId}/upload`, {
        method: "POST",
        body: JSON.stringify({
          source_type: "pasted_text",
          name: name.trim(),
          markdown_content: text,
          promote_to_library: promoteToLibrary,
        }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece-blocked-sources", pieceId] });
      onDone();
    },
  });

  return (
    <div className="settings-form" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--rule)" }}>
      <div style={{ display: "flex", gap: 6 }}>
        {(["file", "url", "paste"] as UploadMode[]).map((m) => (
          <button
            key={m}
            type="button"
            className="btn-dec btn-review"
            style={{ fontWeight: mode === m ? 700 : 400 }}
            onClick={() => setMode(m)}
          >
            {m === "file" ? "File" : m === "url" ? "URL" : "Paste text"}
          </button>
        ))}
      </div>

      {mode === "file" && (
        <label className="settings-field">
          <span className="settings-label">File (PDF, .docx, .xlsx, .pptx, HTML, text — max 25 MB)</span>
          <input
            type="file"
            className="settings-input"
            accept=".pdf,.docx,.xlsx,.pptx,.html,.htm,.txt,.md"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
      )}

      {mode === "url" && (
        <label className="settings-field">
          <span className="settings-label">Source URL</span>
          <input
            type="url"
            className="settings-input"
            placeholder="https://example.com/report"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>
      )}

      {mode === "paste" && (
        <label className="settings-field">
          <span className="settings-label">Markdown / text</span>
          <textarea
            className="settings-input"
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ height: "auto", padding: "10px 12px", resize: "vertical", fontFamily: "inherit" }}
          />
        </label>
      )}

      <label className="settings-field">
        <span className="settings-label">Name {mode === "paste" ? "(required)" : "(optional)"}</span>
        <input
          className="settings-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Q4 Earnings Brief"
        />
      </label>

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={promoteToLibrary}
          onChange={(e) => setPromoteToLibrary(e.target.checked)}
        />
        Save to library for future reference
      </label>

      {upload.isError && (
        <div className="settings-form-error">
          {upload.error instanceof Error ? upload.error.message : "Upload failed."}
        </div>
      )}

      <div className="settings-form-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={upload.isPending}
          onClick={() => upload.mutate()}
        >
          {upload.isPending ? "Uploading…" : "Upload"}
        </button>
      </div>
    </div>
  );
}
